import { ColDef } from 'ag-grid';
import { ValueFormatterParams } from 'ag-grid/dist/lib/entities/colDef';
import { ValueGetterParams } from 'ag-grid/dist/lib/entities/colDef';
import { DateTimeUtils } from '@blcloud/bl-common';
import { TooltipCellComponent, BlNl2brPipe } from '@blcloud/bl-ng-ui-component';
import { ScheduleEventCellComponent } from '../../shared/schedule-event-cell/schedule-event-cell.component';
import { LabelViewComponent } from './label/label-view.component';
import { CalculateDiv } from '@blcloud/bl-datamodel/enum/common/calculate-div';

const blNl2brPipe = new BlNl2brPipe();

/**
 * コンタクト一覧グリッド設定
 */
export interface IContactListGridSetting {
  /** ページサイズ */
  pageSize?: number;
}

/**
 * コンタクト一覧検索条件
 */
export interface ContactSearchCondition {

  /** コンタクト日付検索区分 */
  contactDateSearchDiv?: string;

  /** 開始日 */
  contactSearchDateStart?: string;

  /** 終了日 */
  contactSearchDateEnd?: string;

  /** 従業員コード */
  employeeCode?: string;

  /** 従業員名カナ */
  employeeName?: string;

  /** 車検 */
  vehicleInspFlag?: boolean;

  /** 点検 */
  inspFlag?: boolean;

  /** その他フラグ（実施内容） */
  contactExecuteItemsFlag?: boolean;

  /** 実施内容選択済み項目 */
  contactExecuteItemCodes?: number[];

  /** スケジュールイベントIDリスト */
  scheduleEventIdList?: string[];

  /** コンタクト抽出区分（全て・未実施・実施） */
  contactExtractionDiv?: string;

  /** 再コンタクトのみ */
  recontactFlag?: boolean;

  /** 成約 */
  contractFlag?: boolean;

  /** 失注 */
  contactLostFlag?: boolean;

  /** その他フラグ（実施結果） */
  contactExecuteResultFlag?: boolean;

  /** 実施結果選択済み項目 */
  contactExecuteResultCodes?: number[];

  /** 顧客ラベル */
  customerLabelCodeList?: number[];

  /** 組織コード */
  organizationCodes?: string[];

  /** コンタクト失注理由コード（API In。例: 000001_90000。単一組織時はコードのみ） */
  contactLostReasonCode?: string[];

  /** コンタクト内容コード（API In。例: 000001_90000。単一組織時はコードのみ） */
  contactItemCode?: string[];

  /** 失注理由2列UIの選択キー（組織コード_コード。In/API と同一。LS 保存・画面復元用） */
  contactLostReasonOrgMasterKeys?: string[];

  /** コンタクト区分2列UIの選択キー（組織コード_コード。In/API と同一。LS 保存・画面復元用） */
  contactItemOrgMasterKeys?: string[];

  /** ローカルストレージ保存時のテナントID（他テナントの LS 復元で「[] 他社受注」が出ないようにする） */
  savedBlTenantId?: string;

  /** コンタクトステータスコード */
  contactStatusCode?: number;

  /** 顧客名称カナ１ */
  customerNameKana1?: string;

  /** 実施手段 */
  contactDivSettingCodeList?: number[];

  /** コンタクト抽出対象MP有無区分 */
  contactExtractMPExistDiv?: string;

  /** 受託リース契約有無区分（検索用） */
  entrustedContractExistDivSearch?: string;

  /** 顧客DM案内可否 */
  customerDmSendDiv?: string;

  /** 車両DM案内可否 */
  vehicleDmSendDiv?: string;

  /** 抽出結果の並び順 */
  sortOrderDiv?: string;

  /** 期間区分（ローカルストレージ復元時に相対期間の日付を再計算する） */
  periodDiv?: string;
}

/**
 * 抽出結果の並び順区分
 */
export enum SortOrderDiv {
  /** 顧客名カナ */
  CustomerNameKana = '1',
  /** コンタクト予定日 */
  ContactScheduleDate = '2',
}

/**
 * 抽出結果の並び順リスト
 */
export const SORT_ORDER_DIV_LIST = [
  { value: SortOrderDiv.CustomerNameKana, text: '顧客名カナ' },
  { value: SortOrderDiv.ContactScheduleDate, text: 'コンタクト予定日' },
];

/**
 * 組織別マスタの種類（失注理由・コンタクト区分の2列UIで扱うマスタ）
 */
export enum OrgMasterKind {
  /** 失注理由 */
  LostReason = 'lostReason',
  /** コンタクト内容（コンタクト区分） */
  ContactItem = 'contactItem',
}

/**
 * 組織別マスタの候補（2列UIの右列1行）
 */
export interface IOrgMasterItem {
  /** 選択キー（コード__組織コード） */
  key: string;
  /** 組織コード */
  organizationCode: string;
  /** コード */
  code: number;
  /** 名称 */
  name: string;
  /** 表示ラベル（[組織名] 名称） */
  label: string;
}

/** 選択キーのコードと組織コードの区切り文字（フロントエンド用） */
export const ORG_MASTER_KEY_SEPARATOR = '__';

/** In 条件の組織コードとコードの区切り文字（バックエンド用） */
export const ORG_CODE_PAIR_SEPARATOR = '_';

/** 2列UIの選択キーを組み立てます（組織コード_コード）。 */
export function buildOrgMasterSelectionKey(organizationCode: string, code: number | string): string {
  return String(organizationCode).trim() + ORG_CODE_PAIR_SEPARATOR + String(code);
}

/** 2列UI選択キーを正規化します */
export function normalizeOrgMasterSelectionKey(key: string): string {
  // 選択キーを正規化
  const trimmed = String(key || '').trim();
  if (trimmed === '') {
    return trimmed;
  }
  // 旧 code__組織 の区切りを取得
  const legacySepIndex = trimmed.indexOf(ORG_MASTER_KEY_SEPARATOR);
  if (legacySepIndex > 0) {
    // 旧 code__組織 の区切りの前がコード、後ろが組織コード
    const code = trimmed.substring(0, legacySepIndex);
    const orgCode = trimmed.substring(legacySepIndex + ORG_MASTER_KEY_SEPARATOR.length);
    if (orgCode !== '' && code !== '') {
      return buildOrgMasterSelectionKey(orgCode, code);
    }
  }
  // 選択キーをそのまま返却
  return trimmed;
}

/**
 * 選択キーが検索組織のいずれかに属するか。
 * 検索対象組織が未確定（空）のときは true（判定保留＝フィルタで除外しない）。
 */
export function isOrgMasterSelectionKeyInOrganizations(key: string, organizationCodes: string[]): boolean {
  const normalized = normalizeOrgMasterSelectionKey(String(key || '').trim());
  if (normalized === '') {
    return false;
  }
  const orgCodes = (organizationCodes || []).map(c => String(c).trim()).filter(c => c !== '');
  if (orgCodes.length === 0) {
    return true;
  }
  if (normalized.indexOf(ORG_CODE_PAIR_SEPARATOR) >= 0) {
    return orgCodes.some(org => normalized.startsWith(org + ORG_CODE_PAIR_SEPARATOR));
  }
  return orgCodes.length === 1;
}

/** 選択キーに該当するコードが無いときに検索In条件へ載せる値（抽出結果を0件にする） */
export const ORG_MASTER_NOT_FOUND_CODE = '-1';

/** コンタクト一覧検索条件のローカルストレージキー */
export const CONTACT_LIST_PAGE_SEARCH_PARAM_KEY = 'CONTACT_LIST_PAGE_SEARCH_PARAM';

/** コンタクト一覧グリッド設定のローカルストレージキー */
export const CONTACT_LIST_GRID_SETTING_KEY = 'contactListGridSetting';

/**
 * グリッド列定義
 */
export const COLUMN_DEFS: ColDef[] = [
  {
    headerName: '更新状況',
    field: 'gridData.updateStatus',
    suppressMovable: false,
    width: 180,
    minWidth: 180,
    cellStyle: {
      'color': '#cc0000',
      'font-family': '"Helvetica Neue",Helvetica,Arial,sans-serif'
    },
    hide: true
  },
  {
    headerName: '再',
    field: 'gridData.reContactFlag',
    suppressMovable: false,
    suppressResize: true,
    width: 55,
    minWidth: 55,
    maxWidth: 55,
    cellStyle: {
      'text-align': 'center',
      'justify-content': 'center',
    },
    headerClass: 'ag-header-cell__schedule',
    valueFormatter: (params: ValueFormatterParams): string => {
      return params.value ? '再' : '';
    },
  },
  {
    headerName: '済',
    field: 'gridData.implementedFlag',
    suppressMovable: false,
    suppressResize: true,
    width: 55,
    minWidth: 55,
    maxWidth: 55,
    cellStyle: {
      'text-align': 'center',
      'justify-content': 'center',
    },
    headerClass: 'ag-header-cell__schedule',
    valueFormatter: (params: ValueFormatterParams): string => {
      return params.value ? '済' : '';
    },
  },
  {
    headerName: '実施予定日',
    field: 'gridData.scheduleDate',
    suppressMovable: false,
    width: 200,
    minWidth: 200,
    cellRendererFramework: ScheduleEventCellComponent,
    headerClass: 'ag-header-cell__schedule',
  },
  {
    headerName: '時間', // 実施予定時間
    field: 'gridData.scheduleTime',
    suppressMovable: false,
    width: 70,
    minWidth: 70,
    headerClass: 'ag-header-cell__schedule',
  },
  {
    headerName: 'コンタクト区分名称',
    field: 'gridData.contactItemName',
    suppressMovable: false,
    width: 180,
    minWidth: 180,
    headerClass: 'ag-header-cell__schedule',
  },
  {
    headerName: '実施内容',
    field: 'gridData.executeItem',
    suppressMovable: false,
    width: 180,
    minWidth: 180,
    headerClass: 'ag-header-cell__schedule',
    cellRendererFramework: TooltipCellComponent,
    valueGetter: (args: ValueGetterParams) => {
      return {
        value: args.node.data.gridData.executeItem,
        tooltip: blNl2brPipe.transform(args.node.data.gridData.executeItem),
        placement: 'bottom',
        isHtmlTooltip: true,
      };
    },
    comparator: tooltipSortComparator,
  },
  {
    headerName: '基準日',
    field: 'gridData.contactStandartDate',
    suppressMovable: false,
    width: 200,
    minWidth: 200,
    headerClass: 'ag-header-cell__schedule',
  },
  {
    headerName: '実施手段',
    field: 'gridData.contactMethodName',
    suppressMovable: false,
    width: 120,
    minWidth: 120,
    headerClass: 'ag-header-cell__schedule',
  },
  {
    headerName: '実施担当',
    field: 'gridData.contactEmployeeName',
    suppressMovable: false,
    width: 240,
    minWidth: 240,
    headerClass: 'ag-header-cell__schedule',
  },
  {
    headerName: '顧客名',
    field: 'gridData.customerName',
    suppressMovable: false,
    width: 140,
    minWidth: 140,
    headerClass: 'ag-header-cell__schedule',
  },
  {
    headerName: '車種',
    field: 'gridData.vehicleModelFullWName',
    suppressMovable: false,
    width: 140,
    minWidth: 140,
    headerClass: 'ag-header-cell__schedule',
  },
  {
    headerName: '登録番号',
    field: 'gridData.displayNumPlate',
    suppressMovable: false,
    width: 120,
    minWidth: 120,
    headerClass: 'ag-header-cell__schedule',
  },
  {
    headerName: 'ランク',
    field: 'gridData.customerRankName',
    suppressMovable: false,
    width: 90,
    minWidth: 90,
    headerClass: 'ag-header-cell__schedule',
  },
  {
    headerName: '拠点',
    field: 'gridData.baseName',
    suppressMovable: false,
    width: 180,
    minWidth: 180,
    headerClass: 'ag-header-cell__schedule',
  },
  {
    headerName: 'ラベル',
    field: 'gridData.customerLabel',
    suppressMovable: false,
    width: 100,
    minWidth: 100,
    cellRendererFramework: LabelViewComponent,
    headerClass: 'ag-header-cell__schedule',
  },
  {
    headerName: 'リース車両',
    field: 'gridData.entrustedContractExistDiv',
    suppressMovable: false,
    width: 100,
    minWidth: 100,
    headerClass: 'ag-header-cell__schedule',
    valueFormatter: (params: ValueFormatterParams): string => {
      if (params.data.gridData.contact.taskInfo) {
        return params.data.gridData.contact.taskInfo.entrustedContractExistDiv === CalculateDiv.Present
         ? '<span class="is-exists-lease">●</span>' : '';
      } else if (params.data.gridData.contact.contactHistory) {
        return params.data.gridData.contact.contactHistory.entrustedContractExistDiv === CalculateDiv.Present
         ? '<span class="is-exists-lease">●</span>' : '';
      } else {
        return '';
      }
    },
    cellStyle: {
      'text-align': 'center',
      'justify-content': 'center',
    },
  },
  {
    headerName: '自賠責特殊用途',
    field: 'gridData.vcleAddInfo1',
    suppressMovable: false,
    width: 200,
    minWidth: 200,
    headerClass: 'ag-header-cell__schedule',
    hide: true
  },
  {
    headerName: '自動車の追加情報2',
    field: 'gridData.vcleAddInfo2',
    suppressMovable: false,
    width: 200,
    minWidth: 200,
    headerClass: 'ag-header-cell__schedule',
  },
  {
    headerName: '自動車の追加情報',
    field: 'gridData.vcleAddInfo3',
    suppressMovable: false,
    width: 200,
    minWidth: 200,
    headerClass: 'ag-header-cell__schedule',
  },
  {
    headerName: '初度登録年月',
    field: 'gridData.firstRegistYearMonth',
    suppressMovable: false,
    width: 170,
    minWidth: 170,
    headerClass: 'ag-header-cell__schedule',
  },
  {
    headerName: '車検証備考',
    field: 'gridData.vcleInspCertifRemarksMemo',
    suppressMovable: false,
    width: 200,
    minWidth: 200,
    headerClass: 'ag-header-cell__schedule',
    cellRendererFramework: TooltipCellComponent,
    valueGetter: (args: ValueGetterParams) => {
      const text = args.node.data.gridData.vcleInspCertifRemarksMemo || '';
      return {
        value: text,
        tooltip: blNl2brPipe.transform(text),
        placement: 'bottom',
        isHtmlTooltip: true,
      };
    },
    comparator: tooltipSortComparator,
  },
  {
    headerName: '受託リース契約終了年月',
    field: 'gridData.entrustedContractTermEndYearMonth',
    suppressMovable: false,
    width: 200,
    minWidth: 200,
    headerClass: 'ag-header-cell__schedule',
  },
  {
    headerName: '車検満期日',
    field: 'gridData.vcleInspDueDate',
    suppressMovable: false,
    width: 170,
    minWidth: 170,
    headerClass: 'ag-header-cell__schedule',
  },
  {
    headerName: '車両備考1',
    field: 'gridData.customerVehicleRemarksContent1',
    suppressMovable: false,
    headerClass: 'ag-header-cell__schedule',
    width: 200,
    hide: true
  },
  {
    headerName: '車両備考2',
    field: 'gridData.customerVehicleRemarksContent2',
    suppressMovable: false,
    headerClass: 'ag-header-cell__schedule',
    width: 200,
    hide: true
  },
  {
    headerName: '車両備考3',
    field: 'gridData.customerVehicleRemarksContent3',
    suppressMovable: false,
    headerClass: 'ag-header-cell__schedule',
    width: 200,
    hide: true
  },
  {
    headerName: '車両備考4',
    field: 'gridData.customerVehicleRemarksContent4',
    suppressMovable: false,
    headerClass: 'ag-header-cell__schedule',
    width: 200,
    hide: true
  },
  {
    headerName: '車両備考5',
    field: 'gridData.customerVehicleRemarksContent5',
    suppressMovable: false,
    headerClass: 'ag-header-cell__schedule',
    width: 200,
    hide: true
  },
  {
    headerName: '車両備考6',
    field: 'gridData.customerVehicleRemarksContent6',
    suppressMovable: false,
    headerClass: 'ag-header-cell__schedule',
    width: 200,
    hide: true
  },
  {
    headerName: '車両備考7',
    field: 'gridData.customerVehicleRemarksContent7',
    suppressMovable: false,
    headerClass: 'ag-header-cell__schedule',
    width: 200,
    hide: true
  },
  {
    headerName: '車両備考8',
    field: 'gridData.customerVehicleRemarksContent8',
    suppressMovable: false,
    headerClass: 'ag-header-cell__schedule',
    width: 200,
    hide: true
  },
  {
    headerName: '車両備考9',
    field: 'gridData.customerVehicleRemarksContent9',
    suppressMovable: false,
    headerClass: 'ag-header-cell__schedule',
    width: 200,
    hide: true
  },
  {
    headerName: '車両備考10',
    field: 'gridData.customerVehicleRemarksContent10',
    suppressMovable: false,
    headerClass: 'ag-header-cell__schedule',
    width: 200,
    hide: true
  },
  {
    headerName: '結果区分',
    field: 'gridData.executeResultDivName',
    suppressMovable: false,
    width: 120,
    minWidth: 120,
    headerClass: 'ag-header-cell__result',
  },
  {
    headerName: '実施日',
    field: 'gridData.implementDate',
    suppressMovable: false,
    width: 200,
    minWidth: 200,
    headerClass: 'ag-header-cell__result',
  },
  {
    headerName: '時間', // 実施時間
    field: 'gridData.implementTime',
    suppressMovable: false,
    width: 70,
    minWidth: 70,
    valueFormatter: timeFormatter,
    headerClass: 'ag-header-cell__result',
  },
  {
    headerName: '実施結果',
    field: 'gridData.executeResult',
    suppressMovable: false,
    width: 160,
    minWidth: 160,
    headerClass: 'ag-header-cell__result',
  },
  {
    headerName: '失注理由',
    field: 'gridData.lostReason',
    suppressMovable: false,
    width: 120,
    minWidth: 120,
    headerClass: 'ag-header-cell__result',
  },
  {
    headerName: 'メモ',
    field: 'gridData.contactComment',
    suppressMovable: false,
    width: 280,
    minWidth: 280,
    headerClass: 'ag-header-cell__result',
  },
];


/**
 * 時刻フォーマッタ
 * @param params ValueFormatterParams
 * @returns フォーマット文字列
 */
function timeFormatter(params: ValueFormatterParams): string {
  return params.value ? DateTimeUtils.toSubdivideDateTime(params.value).exSecondTime : null;
}

/**
 * ツールチップセル：ソート処理の文字列比較を実施する
 * @param valueA 比較対象A
 * @param valueB 比較対象B
 */
function tooltipSortComparator(valueA: { value: string; tooltip: string }, valueB: { value: string; tooltip: string }) {
  const textA = valueA.value ? valueA.value : '';
  const textB = valueB.value ? valueB.value : '';

  if (textA === textB) {
    return 0;
  } else if (textA === '') {
    return -1;
  } else if (textB === '') {
    return 1;
  } else {
    return textA > textB ? 1 : -1;
  }
}

/**
 * バブルファンクションロール定義
 */
export enum BubbleFnRole {
  /** 初期表示設定 */
  ContactInitSetting = 1000,
  /** 背景色設定 */
  BgColorSetting = 2000,
  /** 列表示設定 */
  ColumnSetting = 3000,
}



// WEBPACK FOOTER //
// ./src/app/page/contact-list-page/contact-list-page.interface.ts